"use client";
import { Suspense, useMemo } from "react";
import { PopularBrandItem, Range, TranslationData } from "@/common/lib/types";
import BrandList from "../itemLists/BrandList";
import ColorList from "../itemLists/ColorList";
import LenseColorList from "../itemLists/LenseColorList";
import SgLensColorFilter from "../itemLists/SgLensColorFilter";
import Price from "../itemLists/Price";
import TextList from "../itemLists/TextList";
import BooleanGroupList from "../itemLists/BooleanGroupList";
import { FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { facetTypeConstant } from "@/apiUtils/constants";

export interface IProps {
  exposed?: boolean;
  selectedTab: string;
  filter?: FilteredFacet;
  searchParams: Record<string, string>;
  priceRange?: Range;
  translations: TranslationData;
  popularBrands?: { title: string; brands: PopularBrandItem[] };
  lang: string;
  onChange: (key: string, value: string) => void;
}

type RenderMode = "price" | "brands" | "clColor" | "lensColor" | "color" | "text";

function detectRenderMode(filter: FilteredFacet): RenderMode {
  const { filterItemType, facetMapping, facetAttributeEn } = filter;

  // Price
  if (filterItemType === "price") return "price";

  if (facetAttributeEn?.includes("brand.name")) return "brands";

  const sample = facetMapping?.[0];
  if (!sample) return "text";

  // Color — items have hexCode
  if (sample.hexCode) {
    if (facetAttributeEn?.includes("clColor")) return "clColor";
    if (facetAttributeEn?.includes("lenseColor")) return "lensColor";
    return "color";
  }

  // Everything else — TextList handles image, boolean toggle internally
  return "text";
}

const TabContents = ({
  selectedTab,
  exposed = false,
  filter,
  searchParams,
  priceRange,
  translations,
  popularBrands,
  lang,
  onChange,
}: IProps) => {
  const filterKey = useMemo(() => filter?.filterParams ?? "_", [filter?.filterParams]);

  const selectedValues = useMemo(
    () => searchParams?.[filterKey]?.split("|") || [],
    [searchParams, filterKey],
  );

  if (!filter?.facetMapping) return <></>;

  const itemTranslations = {
    singleItem: translations?.plp?.plp_filter_option_item_count_single_label || "",
    multipleItem: translations?.plp?.plp_filter_option_item_count_multiple_label || "",
  };

  const mode = detectRenderMode(filter);

  // ── Price ──────────────────────────────────────────────────────────────────
  if (mode === "price") {
    return (
      <Suspense fallback={<></>}>
        <Price
          min={priceRange?.min || 0}
          max={priceRange?.max || 0}
          priceCounts={filter?.priceCounts || {}}
          onChange={onChange}
        />
      </Suspense>
    );
  }

  // ── Brands — with search bar + popular brands section ─────────────────────
  if (mode === "brands") {
    return (
      <BrandList
        facet={filter?.facetMapping}
        selectedValues={selectedValues}
        onClick={(value) => onChange(filterKey, value)}
        popularBrands={popularBrands}
        col={exposed ? 2 : 1}
        translations={{
          ...itemTranslations,
          searchText: translations?.plp?.plp_brand_search_placeholder_label || "",
        }}
      />
    );
  }

  // ── CL Color ───────────────────────────────────────────────────────────────
  if (mode === "clColor") {
    return (
      <LenseColorList
        facet={filter?.facetMapping}
        selectedValues={selectedValues}
        translations={itemTranslations}
        onClick={(value) => onChange(filterKey, value)}
      />
    );
  }

  // ── Sunglasses Lens Color ──────────────────────────────────────────────────
  if (mode === "lensColor") {
    return (
      <SgLensColorFilter
        facet={filter?.facetMapping}
        selectedValues={selectedValues}
        translations={itemTranslations}
        onClick={(value) => onChange(filterKey, value)}
      />
    );
  }

  // ── Frame Color / any new color filter ────────────────────────────────────
  if (mode === "color") {
    return (
      <ColorList
        facet={filter?.facetMapping}
        selectedValues={selectedValues}
        translations={itemTranslations}
        onClick={(value) => onChange(filterKey, value)}
      />
    );
  }

  // ── Text (shape/fit/material/gender/boolean/any new filter) ───────────────

  if (filter.isBooleanGroup && Array.isArray(filter.booleanMappings)) {
    return (
      <BooleanGroupList
        booleanMappings={filter.booleanMappings}
        facetMapping={filter.facetMapping}
        searchParams={searchParams}
        translations={itemTranslations}
        onChange={onChange}
      />
    );
  }

  const resolvedFacetType = filter.facetAttributeEn?.includes("clPerPackSize")
    ? facetTypeConstant.PACK_FILTER
    : filter.filterParams || filter.type;

  return (
    <TextList
      facetName={filter?.name?.[lang] || ""}
      facetType={resolvedFacetType}
      facet={filter?.facetMapping}
      selectedValues={selectedValues}
      translations={itemTranslations}
      lang={lang}
      showIcons={filter?.filterType === "image"}
      onClick={(value) => onChange(filterKey, value)}
    />
  );
};

export default TabContents;
